import type { Company } from "@domain/CRM/company";
import type { Grower } from "@domain/CRM/grower";
import type { Job } from "@domain/CRM/job";
import type { JobReady } from "@domain/CRM/job";
import type { User } from "@domain/Auth";
import { applyCollectBoundary } from "@domain/CRM/job";
import type { LogData } from "@rogoag/shared-ts/dist/logger";
import type { ILogger } from "@ports/logger/ILogger";

export interface MarkJobsReadyUseCasesDeps {
	createJobsReady(form: Partial<JobReady>): Promise<JobReady>;
	updateJobs(jobs: Job[], compressed?: boolean): Promise<void>;
	/** Optional read ports — only the page wires them; the popover doesn't need them. */
	getGrowersWithUnreadyJobs?(company: Company): Promise<Grower[]>;
	getJobsForGrowers?(growers: Grower[]): Promise<Job[]>;
	logger: ILogger;
}

/**
 * Mark a batch of jobs as ready: persists a JobReady record (who/when/which
 * jobs) and applies the `collectBoundary` domain transformation to each
 * selected job before bulk-updating them.
 *
 * The two writes happen in sequence — the JobReady record is created first
 * so the workflow audit log is intact even if the bulk Job update fails.
 * Callers see a single thrown error on either failure; recovery is the
 * UI's responsibility.
 */
export interface MarkJobsReadyUseCases {
	/** Throws if `selectedJobs` is empty — the caller's UI guards that, but
	 *  we preserve the existing contract. */
	submitForm(selectedJobs: Job[], user?: User): Promise<void>;
	loadGrowersWithUnreadyJobs(company: Company): Promise<Grower[]>;
	loadJobsForGrowers(growers: Grower[]): Promise<Job[]>;
}

export async function submitForm(deps: MarkJobsReadyUseCasesDeps, selectedJobs: Job[], user?: User): Promise<void> {
	if (!selectedJobs || selectedJobs.length === 0) {
		throw new Error("selectedJobs.length == 0");
	}
	const jobIds = selectedJobs.filter((j) => !!j).map((j) => j.id);
	const logData: LogData = {
		function: "markJobsReady.submitForm",
		airtable_record_id: jobIds.join(","),
		airtable_table: "Jobs",
	};
	deps.logger.log("Submitting Job Ready form", logData);

	const jobReadyForm: Partial<JobReady> = {
		name: "",
		jobIds,
		submitter: user?.name ?? "",
	};

	// Audit-warn if any selected job is already marked — preserves the
	// pre-existing diagnostic surface.
	for (const job of selectedJobs) {
		if (job?.isMarkedReady) {
			deps.logger.warning(`Job ${job.id} already marked as ready, but an additional Job Ready form is being attached`, {
				...logData,
				airtable_record_id: job.id,
			});
		}
	}

	await deps.createJobsReady(jobReadyForm);
	deps.logger.log("Job Ready form submitted successfully", logData);

	for (const job of selectedJobs) {
		applyCollectBoundary(job);
	}

	await deps.updateJobs(selectedJobs, true);
	deps.logger.log("Jobs updated successfully after marking as ready", logData);
}

export function loadGrowersWithUnreadyJobs(deps: MarkJobsReadyUseCasesDeps, company: Company): Promise<Grower[]> {
	if (!deps.getGrowersWithUnreadyJobs) {
		throw new Error("markJobsReady: getGrowersWithUnreadyJobs dep not wired.");
	}
	return deps.getGrowersWithUnreadyJobs(company);
}

export function loadJobsForGrowers(deps: MarkJobsReadyUseCasesDeps, growers: Grower[]): Promise<Job[]> {
	if (!deps.getJobsForGrowers) {
		throw new Error("markJobsReady: getJobsForGrowers dep not wired.");
	}
	return deps.getJobsForGrowers(growers);
}

export function makeMarkJobsReadyUseCases(deps: MarkJobsReadyUseCasesDeps): MarkJobsReadyUseCases {
	return {
		submitForm: (selectedJobs, user) => submitForm(deps, selectedJobs, user),
		loadGrowersWithUnreadyJobs: (company) => loadGrowersWithUnreadyJobs(deps, company),
		loadJobsForGrowers: (growers) => loadJobsForGrowers(deps, growers),
	};
}
